import { Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { OSStep } from "@/molecules/OSStep"
import {
  DeployTab,
  TitleStepDeployTab,
  type CommonDeployTabProps
} from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import { DeploymentKeyButton } from "@/pages/DeploymentsPage/components/molecules/DeploymentKeyButton"
import {
  ScriptDeployCommand,
  type ScriptDeployCommandProps
} from "@/pages/DeploymentsPage/components/organisms/ScriptDeployCommand"
import { AZURE_APP_PLATFORMS } from "@/pages/DeploymentsPage/const"
import { useContainerImageSteps } from "@/pages/DeploymentsPage/hooks/useContainerImageSteps"
import {
  DEPLOY_SCRIPTS,
  getIdentityDefaultValues,
  getSettingsDefaultValues,
  IDENTITY_SCRIPTS
} from "./helpers"

import styles from "./AzureAppsTab.module.scss"

// The deployment key input of every deploy script gets the generate-key button
const DEPLOY_LINE_BUTTONS: ScriptDeployCommandProps<"deploymentKey">["lineButtons"] =
  { deploymentKey: DeploymentKeyButton }

export const AzureAppsTab = (props: CommonDeployTabProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "deployments" })
  const { t: tGeneral } = useTranslation("translation", {
    keyPrefix: "general"
  })
  const getContainerImageSteps = useContainerImageSteps(props)

  // The app platform is selected first, like the Lambda architecture. The
  // recommended identity step comes before deploy: the deploy commands update an
  // existing app, so its identity can be granted first.
  const {
    items,
    isWindows: _isWindows,
    ...rest
  } = OSStep({
    classes: { select: styles.platformSelect },
    options: AZURE_APP_PLATFORMS.map((platform) => ({
      label: t(`azureApps.platform.${platform}`),
      value: platform
    })),
    placeholder: t("azureApps.selectPlatform.placeholder"),
    waitingTitle: t("azureApps.selectPlatform.waiting"),
    getSteps: ({ OSPlatform: platform }) => [
      // App Service, Functions and Container Apps run linux/amd64
      ...getContainerImageSteps("kortex-azure", "linux/amd64", {
        download: t("azureApps.downloadStep"),
        dockerfile: t("azureApps.dockerfileStep"),
        build: t("azureApps.buildStep")
      }),
      {
        title: (
          <TitleStepDeployTab
            prefix={tGeneral("recommended")}
            subTitle={t("azureApps.identitySubtitle")}
          >
            {`- ${t("azureApps.identityStep")}`}
          </TitleStepDeployTab>
        ),
        description: (
          <ScriptDeployCommand
            {...IDENTITY_SCRIPTS[platform]}
            getDefaultValues={getIdentityDefaultValues}
            {...props}
          />
        ),
        status: "process"
      },
      {
        title: (
          <TitleStepDeployTab prefix={tGeneral("deploy")}>
            {`- ${t("azureApps.deployStep")}`}
          </TitleStepDeployTab>
        ),
        description: (
          <ScriptDeployCommand
            {...DEPLOY_SCRIPTS[platform]}
            lineButtons={DEPLOY_LINE_BUTTONS}
            getDefaultValues={getSettingsDefaultValues}
            {...props}
          />
        ),
        status: "process"
      }
    ],
    getMessage: () => (
      <span>
        <Text>
          {t("azureApps.alert.supported")}
          <br />
          {t("kubernetes.alert.text")}
        </Text>
        <br />
        <b>{t("kubernetes.alert.note")}</b>
      </span>
    )
  })

  return <DeployTab steps={items} {...rest} />
}
